import { Color } from "@playlive/overlay-core/colors";
import {
  DonorListOverlay,
  DonorListOverlayBackgroundColors,
} from "@playlive/overlay-core/overlays/donor-list";
import { deepSignal } from "deepsignal";
import { useMemo } from "preact/hooks";
import NumberInput from "@/components/input/number.input.jsx";
import NumberSliderInput from "@/components/input/number-slider.input.jsx";
import Picker from "@/components/input/picker.input.jsx";
import YesNoButtonInput from "@/components/input/yes-no-button.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import HorizontalCustomizeWrapper from "@/components/overlays/wrapper/horizonal-customize-wrapper.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import useCommon from "@/hooks/useCommon.js";
import { isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  donorListBackgroundColor: Color.DEFAULT_RED,
  donorListDisplayIncentives: "yes",
  donorListOrientation: "vertical",
  donorListAlign: "left",
  donorListAmount: 5,
  displayTimer: "0",
});
/** @typedef {import('@playlive/overlay-core').DonorListItem} DonorListItem **/

export default function DonorListOverlayPage() {
  const useBetaOverlays = isYes(import.meta.env.VITE_BETA_OVERLAYS || "no");
  const { overlayInfo, overlayURL, setConfigState } = useCommon({
    config,
    forceTiltify: true,
    urlBase: useBetaOverlays ? "overlays/donorlist" : "donorlist",
  });

  // Custom onChange handler for displayTimer to convert 31 to 0
  const handleDisplayTimerChange = (event) => {
    const value = event.target.value;
    const convertedEvent = {
      ...event,
      target: {
        ...event.target,
        value: value === 31 || value === "31" ? "0" : value,
      },
    };
    setConfigState(convertedEvent);
  };

  // Convert stored value back to slider display value (0 -> 31)
  const getSliderDisplayValue = () => {
    return config.displayTimer === "0" || config.displayTimer === 0 ? 31 : config.displayTimer;
  };

  const showAlignmentPicker = config.donorListOrientation === "vertical";

  const orientations = [
    { value: "vertical", name: "Vertical" },
    { value: "horizontal", name: "Horizontal" },
  ];

  const alignments = [
    { value: "left", name: "Left" },
    { value: "right", name: "Right" },
  ];

  const _timerOptions = useMemo(
    () => [
      {
        value: "5",
        name: "Five minutes",
      },
      {
        value: "10",
        name: "Ten minutes",
      },
      {
        value: "15",
        name: "Fifteen minutes",
      },
      {
        value: "0",
        name: "Always",
      },
    ],
    [],
  );

  /**
   *
   * @type {Array<DonorListItem>}
   */
  const demoDonors = [
    {
      rewardName: "10 Push-ups",
      amount: 250,
      id: "1",
      name: "Stacey",
      optionName: "Pineapple",
    },
    {
      amount: 200,
      name: "Mike",
      id: "2",
    },
    {
      amount: 10,
      name: "Steve",
      id: "3",
      causeName: "New Toy for Hospital Play Areas",
    },
    {
      amount: 250,
      name: "Paul",
      id: "4",
    },
  ];

  function filterColors(color) {
    return DonorListOverlayBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row! overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <ScaledOverlayWrapper allowScaling={false} addPadding={false}>
            <DonorListOverlay
              backgroundColor={config.donorListBackgroundColor}
              alignment={config.donorListAlign}
              displayIncentives={isYes(config.donorListDisplayIncentives)}
              showDonors={config.donorListAmount}
              orientation={config.donorListOrientation}
              donors={demoDonors}
              displayTimer={config.displayTimer}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CustomizeSection title="Appearance">
            <ColorPickerLabel color={config.donorListBackgroundColor} />

            <ColorPicker
              fieldName="donorListBackgroundColor"
              currentColor={config.donorListBackgroundColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <NumberInput
              name="donorListAmount"
              value={Number(config.donorListAmount)}
              placeholder="5"
              onChange={setConfigState}
              isCurrency={false}
              maxLength={4}
              suffix="donors"
              labelText="Donor Count"
              description="Amount of donors to display"
            />

            <YesNoButtonInput
              mode="checkbox"
              onChange={setConfigState}
              currentValue={config.donorListDisplayIncentives}
              name="donorListDisplayIncentives"
              label="Display claimed rewards and polls"
            />
          </CustomizeSection>

          <CustomizeSection title="Position" className="grow">
            <Picker
              items={orientations}
              fieldName="donorListOrientation"
              onChange={setConfigState}
              currentValue={config.donorListOrientation}
              labelText="Orientation"
            />

            <Picker
              disabled={!showAlignmentPicker}
              items={alignments}
              fieldName="donorListAlign"
              onChange={setConfigState}
              currentValue={config.donorListAlign}
              labelText="Alignment"
              description="Only available in the vertical orientation"
            />

            <NumberSliderInput
              fieldName="displayTimer"
              maxValue={31}
              minValue={1}
              disabled={config.sound === "no"}
              step={1}
              labelText="Visibility Timer"
              onChange={handleDisplayTimerChange}
              value={getSliderDisplayValue()}
              suffix={config.displayTimer === "1" ? " minute" : " minutes"}
              description="Set how long your items in your donor list will show, or keep them always visible."
            />
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <LinkSetup
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        obsDimensions={overlayInfo.obsDimensions}
        orientation={config.donorListOrientation}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        finalStepSlotPosition="middle"
        showCopyDemoModeLinkButton
      />
    </div>
  );
}
